import React, { useState, useEffect, useCallback } from 'react';
import {
  getVolunteers, applyMemberFilters,
  getVolunteerSavedFilters, saveVolunteerFilter, deleteVolunteerFilter,
  generateVolunteersCSV, fmtDate,
  VOLUNTEER_SKILLS, LANGUAGES, SINGAPORE_DISTRICTS,
} from '../../../api/communityApi';

// ── Helpers ────────────────────────────────────────────────────────────────

const VOLUNTEER_ROLE_TYPES = [
  { value: 'Facilitator', label: 'Facilitator' },
  { value: 'Mentor',      label: 'Mentor'      },
  { value: 'Befriender',  label: 'Befriender'  },
  { value: 'Volunteer',   label: 'Volunteer'   },
];

const FILTER_FIELDS = [
  { value: 'district',             label: 'District',     type: 'enum'   },
  { value: 'status',               label: 'Status',       type: 'enum'   },
  { value: 'approvedRoles',        label: 'Role Type',    type: 'array'  },
  { value: 'skills',               label: 'Skills',       type: 'array'  },
  { value: 'languages',            label: 'Languages',    type: 'array'  },
  { value: 'totalHours',           label: 'Total Hours',  type: 'number' },
  { value: 'totalShiftsCompleted', label: 'Total Shifts', type: 'number' },
  { value: 'createdAt',            label: 'Join Date',    type: 'date'   },
];

const OPERATORS_BY_TYPE = {
  string: [{ value: 'equals', label: 'Equals' }, { value: 'not_equals', label: 'Not equals' }],
  enum:   [{ value: 'equals', label: 'Equals' }, { value: 'not_equals', label: 'Not equals' }, { value: 'in', label: 'Is one of' }],
  array:  [{ value: 'contains', label: 'Contains' }, { value: 'not_contains', label: 'Does not contain' }],
  number: [
    { value: 'equals', label: 'Equals' }, { value: 'greater_than', label: 'Greater than' },
    { value: 'less_than', label: 'Less than' },
  ],
  date: [{ value: 'before', label: 'Before' }, { value: 'after', label: 'After' }],
};

const ENUM_VALUES = {
  district:      SINGAPORE_DISTRICTS.map(d => ({ value: d, label: d })),
  status: [
    { value: 'active',           label: 'Active'           },
    { value: 'pending_approval', label: 'Pending Approval' },
    { value: 'inactive',         label: 'Inactive'         },
  ],
  approvedRoles: VOLUNTEER_ROLE_TYPES,
  skills:        VOLUNTEER_SKILLS,
  languages:     LANGUAGES,
};

function StatusBadge({ status }) {
  const cfg =
    status === 'active'           ? { bg: '#D1FAE5', color: '#065F46', label: 'Active'   } :
    status === 'pending_approval' ? { bg: '#FEF3C7', color: '#92400E', label: 'Pending'  } :
                                    { bg: '#F3F4F6', color: '#4B5563', label: 'Inactive' };
  return (
    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: cfg.bg, color: cfg.color }}>
      {cfg.label}
    </span>
  );
}

function filterDescription(filter) {
  const field = FILTER_FIELDS.find(f => f.value === filter.field);
  return `${field?.label || filter.field} ${filter.operator.replace(/_/g, ' ')} "${filter.value}"`;
}

function buildExpressionPreview(filters) {
  const active = filters.filter(f => f.value !== '');
  if (active.length === 0) return '';
  const groups = [];
  let cur = [];
  active.forEach((f, i) => {
    if (i > 0 && f.logic === 'OR') { groups.push(cur); cur = []; }
    cur.push(f);
  });
  groups.push(cur);
  return groups
    .map(g => {
      const parts = g.map(f => filterDescription(f));
      return g.length > 1 ? `(${parts.join(' AND ')})` : parts[0];
    })
    .join(' OR ');
}

function triggerCSVDownload(csv, filename) {
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// ── Logic Connector ────────────────────────────────────────────────────────

function LogicConnector({ logic, onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0 4px 12px' }}>
      <div style={{ width: 2, height: 16, background: '#D1D5DB', borderRadius: 1 }} />
      {['AND', 'OR'].map(l => (
        <button
          key={l}
          onClick={() => onChange(l)}
          style={{
            padding: '2px 12px', fontSize: 11, fontWeight: 700, borderRadius: 4,
            border: '1px solid', cursor: 'pointer',
            borderColor: logic === l ? '#4338CA' : '#D1D5DB',
            background:  logic === l ? '#4338CA' : '#fff',
            color:       logic === l ? '#fff'    : '#6B7280',
            transition: 'all 0.15s',
          }}
        >{l}</button>
      ))}
      <div style={{ width: 2, height: 16, background: '#D1D5DB', borderRadius: 1 }} />
    </div>
  );
}

// ── Filter Row ─────────────────────────────────────────────────────────────

function FilterRow({ filter, onChange, onRemove }) {
  const fieldMeta = FILTER_FIELDS.find(f => f.value === filter.field) || FILTER_FIELDS[0];
  const operators = OPERATORS_BY_TYPE[fieldMeta.type] || OPERATORS_BY_TYPE.string;
  const enumVals  = ENUM_VALUES[filter.field] || null;

  const handleFieldChange = (newField) => {
    const meta = FILTER_FIELDS.find(f => f.value === newField) || FILTER_FIELDS[0];
    const ops  = OPERATORS_BY_TYPE[meta.type] || OPERATORS_BY_TYPE.string;
    onChange({ ...filter, field: newField, operator: ops[0]?.value || 'equals', value: '' });
  };

  return (
    <div className="bb-com-filter-row">
      <select value={filter.field} onChange={e => handleFieldChange(e.target.value)}>
        {FILTER_FIELDS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
      </select>

      <select value={filter.operator} onChange={e => onChange({ ...filter, operator: e.target.value })}>
        {operators.map(op => <option key={op.value} value={op.value}>{op.label}</option>)}
      </select>

      {enumVals ? (
        <select value={filter.value} onChange={e => onChange({ ...filter, value: e.target.value })}>
          <option value="">— Select —</option>
          {enumVals.map(ev => <option key={ev.value} value={ev.value}>{ev.label}</option>)}
        </select>
      ) : fieldMeta.type === 'date' ? (
        <input
          type="date"
          value={filter.value}
          onChange={e => onChange({ ...filter, value: e.target.value })}
        />
      ) : fieldMeta.type === 'number' ? (
        <input
          type="number"
          placeholder="Value"
          value={filter.value}
          onChange={e => onChange({ ...filter, value: e.target.value })}
        />
      ) : (
        <input
          type="text"
          placeholder="Value"
          value={filter.value}
          onChange={e => onChange({ ...filter, value: e.target.value })}
        />
      )}

      <button className="bb-com-filter-remove" onClick={onRemove} title="Remove filter">×</button>
    </div>
  );
}

// ── Filter Builder Tab ─────────────────────────────────────────────────────

function FilterBuilderTab({ volunteers, orgId, adminId, onFilterSaved }) {
  const [filters, setFilters]       = useState([]);
  const [results, setResults]       = useState(null);
  const [saving, setSaving]         = useState(false);
  const [savePrompt, setSavePrompt] = useState(false);
  const [saveName, setSaveName]     = useState('');
  const [saveError, setSaveError]   = useState('');

  const addFilter    = ()           => setFilters(f => [...f, { id: Date.now(), field: 'district', operator: 'equals', value: '', logic: 'AND' }]);
  const updateFilter = (id, upd)   => setFilters(f => f.map(r => r.id === id ? upd : r));
  const setConnector = (id, logic) => setFilters(f => f.map(r => r.id === id ? { ...r, logic } : r));
  const removeFilter = (id)        => setFilters(f => f.filter(r => r.id !== id));

  const applyFilters = () => {
    const active = filters.filter(f => f.value !== '');
    setResults(applyMemberFilters(volunteers, active));
  };

  const handleSave = async () => {
    if (!saveName.trim()) { setSaveError('Please enter a name.'); return; }
    setSaving(true); setSaveError('');
    try {
      const active = filters.filter(f => f.value !== '');
      await saveVolunteerFilter(orgId, { name: saveName, filters: active }, adminId);
      setSavePrompt(false); setSaveName('');
      onFilterSaved && onFilterSaved();
    } catch (e) {
      setSaveError(e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const preview = buildExpressionPreview(filters);

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <button className="bb-btn-outline-sm" onClick={addFilter}>+ Add Filter</button>
      </div>

      {filters.length === 0 && (
        <div style={{ color: '#9CA3AF', fontSize: 13, marginBottom: 16, fontStyle: 'italic' }}>
          No filters added. Click "Add Filter" to start building.
        </div>
      )}

      {filters.map((f, i) => (
        <React.Fragment key={f.id}>
          {i > 0 && (
            <LogicConnector logic={f.logic || 'AND'} onChange={logic => setConnector(f.id, logic)} />
          )}
          <FilterRow
            filter={f}
            onChange={upd => updateFilter(f.id, upd)}
            onRemove={() => removeFilter(f.id)}
          />
        </React.Fragment>
      ))}

      {preview && (
        <div style={{
          marginTop: 12, padding: '8px 14px',
          background: '#F5F3FF', border: '1px solid #DDD6FE',
          borderRadius: 6, fontSize: 12, color: '#5B21B6',
          fontFamily: 'monospace', wordBreak: 'break-word',
        }}>
          {preview}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
        <button className="bb-btn-primary" onClick={applyFilters}>Apply Filters</button>
        {results !== null && filters.length > 0 && (
          <button className="bb-btn-outline-sm" onClick={() => setSavePrompt(true)}>Save Filter</button>
        )}
      </div>

      {savePrompt && (
        <div style={{ marginTop: 16, background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Save Filter As</div>
          {saveError && <div className="bb-alert bb-alert--error" style={{ margin: '0 0 8px' }}>{saveError}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              className="bb-input"
              style={{ flex: 1 }}
              placeholder="Filter name…"
              value={saveName}
              onChange={e => setSaveName(e.target.value)}
            />
            <button className="bb-btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button className="bb-btn-outline-sm" onClick={() => setSavePrompt(false)}>Cancel</button>
          </div>
        </div>
      )}

      {results !== null && (
        <div style={{ marginTop: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 12 }}>
            Results: {results.length} volunteer(s) matched
          </div>
          {results.length > 0 && (
            <>
              <div style={{ marginBottom: 10 }}>
                <button
                  className="bb-btn-outline-sm"
                  onClick={() => triggerCSVDownload(generateVolunteersCSV(results), 'volunteer-filter-results.csv')}
                >
                  Export CSV
                </button>
              </div>
              <table className="bb-com-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Total Hours</th>
                    <th>Shifts</th>
                  </tr>
                </thead>
                <tbody>
                  {results.slice(0, 50).map(v => (
                    <tr key={v.id}>
                      <td style={{ fontWeight: 600 }}>{v.displayName}</td>
                      <td>{v.email || '—'}</td>
                      <td><StatusBadge status={v.status} /></td>
                      <td style={{ fontWeight: 700, color: '#4338CA' }}>{(v.totalHours || 0).toFixed(1)} h</td>
                      <td>{v.totalShiftsCompleted || 0}</td>
                    </tr>
                  ))}
                  {results.length > 50 && (
                    <tr>
                      <td colSpan={5} style={{ textAlign: 'center', color: '#9CA3AF', fontSize: 12, padding: 12 }}>
                        Showing first 50 of {results.length}. Export CSV to view all.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Saved Filters Tab ──────────────────────────────────────────────────────

function SavedFiltersTab({ orgId, adminId, volunteers, refreshKey }) {
  const [savedFilters, setSavedFilters] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');
  const [deleting, setDeleting]         = useState({});

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      setSavedFilters(await getVolunteerSavedFilters(orgId));
    } catch (e) {
      setError(e.message || 'Failed to load saved filters.');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const handleDelete = async (id) => {
    setDeleting(d => ({ ...d, [id]: true }));
    try { await deleteVolunteerFilter(orgId, id); await load(); }
    finally { setDeleting(d => ({ ...d, [id]: false })); }
  };

  const handleExport = (sf) => {
    const results = applyMemberFilters(volunteers, sf.filters || []);
    triggerCSVDownload(
      generateVolunteersCSV(results),
      `vol-filter-${(sf.name || 'export').replace(/\s+/g, '-').toLowerCase()}.csv`,
    );
  };

  if (loading) return <div className="bb-loading-row">Loading saved filters…</div>;
  if (error)   return <div className="bb-alert bb-alert--error">{error}</div>;
  if (!savedFilters.length) return <div className="bb-empty-state">No saved filters yet.</div>;

  return (
    <div>
      {savedFilters.map(sf => (
        <div key={sf.id} style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: '14px 16px', marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#111827', marginBottom: 4 }}>{sf.name}</div>
              <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 6 }}>Created: {fmtDate(sf.createdAt)}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                {(sf.filters || []).map((f, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && (
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
                        background: (f.logic || 'AND') === 'OR' ? '#EDE9FE' : '#DBEAFE',
                        color:      (f.logic || 'AND') === 'OR' ? '#7C3AED' : '#1E40AF',
                      }}>
                        {f.logic || 'AND'}
                      </span>
                    )}
                    <span style={{ fontSize: 11, background: '#EEF2FF', color: '#4338CA', padding: '2px 8px', borderRadius: 12 }}>
                      {filterDescription(f)}
                    </span>
                  </React.Fragment>
                ))}
              </div>
            </div>
            <div className="bb-action-row">
              <button className="bb-btn-outline-sm" onClick={() => handleExport(sf)}>Export CSV</button>
              <button
                className="bb-btn-danger-sm"
                disabled={deleting[sf.id]}
                onClick={() => handleDelete(sf.id)}
              >
                {deleting[sf.id] ? '…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

export default function VolunteerSegmentation({ orgId, adminId }) {
  const [activeTab, setActiveTab]   = useState('filter');
  const [volunteers, setVolunteers] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [savedKey, setSavedKey]     = useState(0);

  useEffect(() => {
    const load = async () => {
      setLoading(true); setError('');
      try {
        const vols = await getVolunteers(orgId);
        setVolunteers(vols.map(v => ({ ...v, approvedRoles: v.volunteerRoles || [] })));
      } catch (e) {
        setError(e.message || 'Failed to load volunteers.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [orgId]);

  const tabs = [
    { key: 'filter', label: 'Filter Builder' },
    { key: 'saved',  label: 'Saved Filters'  },
  ];

  return (
    <div className="bb-prog-tool">
      <div className="bb-prog-tool-header">
        <h2 className="bb-prog-tool-title">Volunteer Segmentation</h2>
        <p className="bb-prog-tool-desc">Build dynamic filters and manage saved volunteer segments.</p>
        <div className="bb-prog-tabs">
          {tabs.map(t => (
            <button
              key={t.key}
              className={`bb-prog-tab${activeTab === t.key ? ' active' : ''}`}
              onClick={() => setActiveTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bb-prog-tab-content">
        {error && <div className="bb-alert bb-alert--error">{error}</div>}
        {loading ? (
          <div className="bb-loading-row">Loading volunteers…</div>
        ) : (
          <>
            {activeTab === 'filter' && (
              <FilterBuilderTab
                volunteers={volunteers}
                orgId={orgId}
                adminId={adminId}
                onFilterSaved={() => setSavedKey(k => k + 1)}
              />
            )}
            {activeTab === 'saved' && (
              <SavedFiltersTab
                orgId={orgId}
                adminId={adminId}
                volunteers={volunteers}
                refreshKey={savedKey}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
